import React from "react";
import { Check } from "lucide-react";

/**
 * Inline confirmation card for the customer portal — renders WITHIN the
 * portal shell so the sidebar + portal top bar stay visible.
 * Used by both:
 *   - In-Depth Assessment payment success
 *   - Scope of Services payment success
 */
const PAGES = {
  assessment: {
    receipt: "In-Depth Assessment · Receipt sent to your email",
    h1: "Your Assessment is Underway.",
    bodyP1: "Thanks — your payment has been received and your Detailed AI Readiness Assessment is now active. Our team will begin reviewing your responses and pulling the deeper analysis that goes well beyond the initial readiness snapshot.",
    bodyP2: "You don't need to do anything else right now. We'll keep you updated through your portal as each phase moves forward.",
    steps: [
      { num: 1, label: "Our team begins your analysis", desc: "We review your responses and data to build your full AI readiness picture across all six organizational dimensions." },
      { num: 2, label: "A follow-up call may be scheduled", desc: "In some cases we'll reach out to clarify a detail or gather one additional input. Expect to hear from us within 1–2 business days." },
      { num: 3, label: "Your Detailed Report is delivered", desc: "Your full AI Readiness Report — pillar scores, growth engine analysis, and your personalized AI Workflow Plan — will appear in your customer portal." },
    ],
    unlock: null,
  },
  scope: {
    receipt: "Scope of Services · $9,999 · Receipt sent to your email",
    h1: "Your Implementation is Now Active.",
    bodyP1: "Your Scope of Services payment has been received. Your top three workflows are locked into the implementation queue and your dedicated 589 Labs delivery team has been notified.",
    bodyP2: "From here forward, every milestone, deliverable, and review will live inside your customer portal — fully transparent, with no waiting on email threads to know where things stand.",
    steps: [
      { num: 1, label: "Kickoff call scheduled within 48 hours", desc: "Your 589 Labs consultant will reach out to confirm your kickoff date and introduce your dedicated implementation lead." },
      { num: 2, label: "First workflows deploy in Days 1–30", desc: "Based on your AI Workflow Plan, we begin with your ready-to-deploy workflows — the fastest path to visible results and measurable ROI." },
      { num: 3, label: "Ongoing reviews and milestone tracking", desc: "You'll have full visibility in your portal and a regular review cadence to track progress, measure outcomes, and plan your next phase." },
    ],
    unlock: {
      title: "Now unlocked in your portal",
      items: [
        { label: "Playbooks", sub: "Step-by-step AI workflow guides for your team" },
        { label: "Roadmap", sub: "Your 30/60/90-day implementation timeline" },
        { label: "Project Tracker", sub: "Live visibility into every milestone and deliverable" },
        { label: "AI Delivery Standard", sub: "Your organization's governance and operating framework" },
      ],
    },
  },
};

export default function PaymentConfirmedInline({ variant = "assessment", onBackToPortal }) {
  const cfg = PAGES[variant] || PAGES.assessment;
  return (
    <div
      className="max-w-[900px] mx-auto py-8"
      style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}
      data-testid={`payment-confirm-${variant}`}
    >
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        .pc-fade { animation: fadeUp 0.6s ease-out both; }
        @media (max-width: 680px) { .pc-h1 { font-size: 30px !important; } .pc-steps { grid-template-columns: 1fr !important; } .pc-unlock { grid-template-columns: 1fr !important; } }
      `}</style>

      <div style={{ textAlign: "center" }}>
        <div className="pc-fade" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 16px", background: "#E4F3EC", color: "#1A6B42", borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
          <Check size={14} strokeWidth={3} />
          Payment Confirmed
        </div>
        <div className="pc-fade" style={{ marginTop: 16, display: "inline-flex", padding: "6px 14px", background: "#fff", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 20, fontSize: 12, color: "#9499A8", fontWeight: 500 }} data-testid="confirm-receipt-pill">
          {cfg.receipt}
        </div>
        <h1 className="pc-fade pc-h1" style={{ marginTop: 22, fontSize: 42, fontWeight: 700, letterSpacing: "-0.6px", lineHeight: 1.12, color: "#0F1117" }} data-testid="confirm-h1">
          {cfg.h1}
        </h1>
        <p className="pc-fade" style={{ marginTop: 18, fontSize: 16, lineHeight: 1.7, color: "#2D3142", maxWidth: 720, marginLeft: "auto", marginRight: "auto" }}>
          {cfg.bodyP1}
        </p>
        <p className="pc-fade" style={{ marginTop: 14, fontSize: 16, lineHeight: 1.7, color: "#2D3142", maxWidth: 720, marginLeft: "auto", marginRight: "auto" }}>
          {cfg.bodyP2}
        </p>

        {cfg.unlock && (
          <div className="pc-fade" style={{ marginTop: 32, background: "#fff", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 16, padding: "26px 28px", animationDelay: "0.16s", textAlign: "left" }} data-testid="confirm-unlock-panel">
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", color: "#5C6070", textTransform: "uppercase" }}>{cfg.unlock.title}</div>
            <div className="pc-unlock" style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 14 }}>
              {cfg.unlock.items.map((u, i) => (
                <div key={i} style={{ padding: "14px 16px", background: "#FAFAF8", border: "1px solid rgba(0,0,0,0.06)", borderRadius: 10 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#0F1117" }}>{u.label}</div>
                  <div style={{ fontSize: 12, color: "#5C6070", marginTop: 2 }}>{u.sub}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="pc-fade" style={{ marginTop: cfg.unlock ? 24 : 36, background: "#fff", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 16, padding: "32px 36px", textAlign: "left", animationDelay: "0.12s" }} data-testid="confirm-steps-card">
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", color: "#5C6070", textTransform: "uppercase" }}>What happens next</div>
          <div className="pc-steps" style={{ marginTop: 18, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 22 }}>
            {cfg.steps.map((s) => (
              <div key={s.num}>
                <div style={{ width: 32, height: 32, background: "#3B4FE4", color: "#fff", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14 }}>{s.num}</div>
                <div style={{ marginTop: 12, fontSize: 14, fontWeight: 700, color: "#0F1117" }}>{s.label}</div>
                <div style={{ marginTop: 6, fontSize: 13, lineHeight: 1.6, color: "#5C6070" }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={onBackToPortal}
          className="pc-fade"
          style={{ marginTop: 36, padding: "14px 30px", background: "#3B4FE4", color: "#fff", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: "pointer", animationDelay: "0.20s" }}
          data-testid="confirm-back-to-portal-btn"
        >
          Back to My Portal →
        </button>
      </div>
    </div>
  );
}
